import { useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { userDetailSelect } from "Layout/slice/selector";
import { setUserDetails, toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { Loader } from "components/Loader";
import { SettingsCameraIcon } from "assets/icons";
import { updateProfile, uploadUserIcon } from "features/Account/service";
import { validationSchema } from "features/Account/validation";
import { PhoneVerify } from "./PhoneVerify";
import {
  PanelContainer,
  TextInput,
  BioInput,
  AvatarButton,
  DarkButton,
  OutlineButton,
} from "./GeneralPanel.styles";

const profileSchema = validationSchema.pick(["first_name", "last_name", "bio"]);

const getInitials = (user) =>
  `${(user?.first_name || "").charAt(0)}${(user?.last_name || "").charAt(
    0
  )}`.toUpperCase();

// The profile fields the existing Profile modal saves, so the API call is the
// same: names and bio change, everything else is sent back as it was.
const buildPayload = (user, values) => ({
  first_name: values.first_name.trim(),
  last_name: values.last_name.trim(),
  email: user.email,
  bio: values.bio.trim(),
  social_links: {
    linkedin: user.social_links?.linkedin || "",
    twitter: user.social_links?.twitter || "",
    instagram: user.social_links?.instagram || "",
  },
});

export const GeneralPanel = () => {
  const dispatch = useDispatch();
  const user = useSelector(userDetailSelect);
  const fileInput = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;
  const [isUploading, setIsUploading] = useState(false);

  const showError = (error) =>
    dispatch(toast({ type: "error", body: parseErrorMessage(error) }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      bio: user?.bio || "",
    },
    validationSchema: profileSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = buildPayload(user, values);
        await updateProfile(payload);
        dispatch(
          setUserDetails({
            ...user,
            first_name: payload.first_name,
            last_name: payload.last_name,
            bio: payload.bio,
          })
        );
        dispatch(toast({ body: "Profile has been updated successfully" }));
        resetForm({ values: { ...values, ...payload } });
      } catch (error) {
        showError(error);
      }
    },
  });

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploading(true);
    try {
      const data = new FormData();
      data.append("user_icon", file);
      const { image_url: imageUrl } = await uploadUserIcon(data);
      await updateProfile({
        ...buildPayload(userRef.current, formik.initialValues),
        user_icon: imageUrl,
      });
      dispatch(setUserDetails({ ...userRef.current, user_icon: imageUrl }));
      dispatch(
        toast({ type: "info", body: "Profile Image uploaded successfully" })
      );
    } catch (error) {
      showError(error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!user) return null;

  const fieldError = (name) => formik.touched[name] && formik.errors[name];

  return (
    <PanelContainer>
      <h3 className="panel-heading">Profile</h3>

      <div
        className="setting-row"
        role="group"
        aria-labelledby="general-avatar-label"
      >
        <div>
          <p className="label" id="general-avatar-label">
            Avatar
          </p>
          <p className="hint">Upload a photo to personalize your account.</p>
        </div>
        <AvatarButton
          type="button"
          aria-label="Change avatar"
          disabled={isUploading}
          onClick={() => fileInput.current?.click()}
        >
          {user.user_icon ? (
            <img className="avatar-image" src={user.user_icon} alt="" />
          ) : (
            getInitials(user)
          )}
          <span className="camera-badge">
            <SettingsCameraIcon />
          </span>
          {isUploading && (
            <span className="avatar-loader">
              <Loader />
            </span>
          )}
        </AvatarButton>
        <input
          ref={fileInput}
          className="d-none"
          type="file"
          accept="image/*"
          data-testid="avatar-input"
          onChange={handleAvatarChange}
        />
      </div>

      <div className="setting-row">
        <label className="label" htmlFor="general-first-name">
          First name
        </label>
        <div className="field">
          <TextInput
            id="general-first-name"
            type="text"
            $invalid={Boolean(fieldError("first_name"))}
            aria-invalid={Boolean(fieldError("first_name"))}
            aria-describedby={
              fieldError("first_name") ? "general-first-name-error" : undefined
            }
            {...formik.getFieldProps("first_name")}
          />
          {fieldError("first_name") && (
            <span className="field-error" id="general-first-name-error">
              {formik.errors.first_name}
            </span>
          )}
        </div>
      </div>

      <div className="setting-row">
        <label className="label" htmlFor="general-last-name">
          Last name
        </label>
        <div className="field">
          <TextInput
            id="general-last-name"
            type="text"
            $invalid={Boolean(fieldError("last_name"))}
            aria-invalid={Boolean(fieldError("last_name"))}
            aria-describedby={
              fieldError("last_name") ? "general-last-name-error" : undefined
            }
            {...formik.getFieldProps("last_name")}
          />
          {fieldError("last_name") && (
            <span className="field-error" id="general-last-name-error">
              {formik.errors.last_name}
            </span>
          )}
        </div>
      </div>

      <div className="setting-row stacked">
        <div>
          <label className="label" htmlFor="general-bio">
            Biography
          </label>
          <p className="hint">
            Tell us a little about yourself and your investing goals.
          </p>
        </div>
        <BioInput
          id="general-bio"
          placeholder="e.g. Long-term investor focused on tech and clean energy."
          $invalid={Boolean(fieldError("bio"))}
          aria-invalid={Boolean(fieldError("bio"))}
          aria-describedby={fieldError("bio") ? "general-bio-error" : undefined}
          {...formik.getFieldProps("bio")}
        />
        {fieldError("bio") && (
          <span className="field-error" id="general-bio-error">
            {formik.errors.bio}
          </span>
        )}
      </div>

      <div
        className="setting-row"
        role="group"
        aria-labelledby="general-email-label"
      >
        <div>
          <p className="label" id="general-email-label">
            Email
          </p>
          <p className="hint">Your email can&apos;t be changed.</p>
        </div>
        <span className="email-value">{user.email}</span>
      </div>

      <div
        className="setting-row stacked last"
        role="group"
        aria-labelledby="general-phone-label"
      >
        <div>
          <p className="label" id="general-phone-label">
            Phone number
          </p>
          <p className="hint">
            Enter a new number and verify it with a one-time code to update it.
          </p>
        </div>
        <PhoneVerify />
      </div>

      {formik.dirty && (
        <div className="actions">
          <OutlineButton
            type="button"
            onClick={() => formik.resetForm()}
            disabled={formik.isSubmitting}
          >
            Cancel
          </OutlineButton>
          <DarkButton
            type="button"
            onClick={formik.handleSubmit}
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formik.isSubmitting ? (
              <Loader className="on-btn" />
            ) : (
              "Save changes"
            )}
          </DarkButton>
        </div>
      )}
    </PanelContainer>
  );
};
